import * as React from 'react';
import { BaseForm } from './Base';
import { ProductDetail } from '../../product';
import Select from './Select';
import { Text } from './Text';

const DetailOptions: string[] = [
  'Varietal',
  'Country',
  'Region',
  'Producer',
  'Farm',
  'Processing Method',
  'Altitude (masl)',
  'Taste Notes',
  'Cup Score',
  'Accuracy',
  'Minimum Weight',
  'Maximum Weight',
  'Bluetooth Support',
  'Bean Hopper Capacity',
  'Grounds Bin Capacity',
  'Burrs Size',
  'Weight',
];

const arrToOpts = (opts: string[]) =>
  opts.map((val: string) => ({
    label: val,
    value: val,
  }));

export interface Props {
  label: string;
  tooltip?: string;
  field: any;
  onValueChange: Function;
  defaultValue: ProductDetail[];
}

const addToArray = (value: ProductDetail[], newValue: ProductDetail) => {
  return Array.from(new Set(value.concat([newValue])));
};

export default function ProductDetails({
  label,
  tooltip,
  field,
  onValueChange,
  defaultValue = [],
}: Props) {
  const [name, setName] = React.useState<string>(null);
  const [value, setValue] = React.useState<string>(null);
  const nameRef = React.useRef(null);
  const valueRef = React.useRef(null);
  const canAdd = name && value;

  const onAdd = () => {
    if (!value || !name) {
      return;
    }

    onValueChange(field, addToArray(defaultValue, { name, value }));
    nameRef.current.value = null;
    valueRef.current.value = null;
  };

  const setDetailValue = (index: number, key: string, value: string) => {
    const newCopy: ProductDetail[] = defaultValue.slice();
    const item = Object.assign({}, newCopy[index], {
      [key]: value,
    });

    newCopy[index] = item;

    onValueChange(field, newCopy);
  };

  const renderItem = (item: ProductDetail, index: number) => (
    <div
      key={`${item.name}-${index}`}
      className="mb2 db black flex justify-between"
    >
      <div className="mr3 flex-auto">
        <Select
          label="Name"
          field={['name']}
          id={`name-${index}`}
          defaultValue={item.name}
          onValueChange={(_: any, newName: string) =>
            setDetailValue(index, 'name', newName)
          }
          options={arrToOpts(DetailOptions.concat(['Other…']))}
        />
        {!DetailOptions.includes(item.name) ? (
          <Text
            label="Enter a value…"
            field={['name']}
            id={`custom-name-${index}`}
            defaultValue={item.name}
            onValueChange={(_: any, value: string) =>
              setDetailValue(index, 'name', value)
            }
          />
        ) : null}
      </div>
      <Text
        className="mr3 flex-auto"
        label="Description"
        field={['description']}
        id={`desc-${index}`}
        defaultValue={item.value}
        onValueChange={(_: any, value: string) =>
          setDetailValue(index, 'value', value)
        }
      />
      <button
        type="button"
        className="red b dib link bg-transparent bw0 pointer dim ma0 pa1 pa3-ns w3"
        onClick={() => {
          onValueChange(
            field,
            defaultValue.filter(
              (arrItem: ProductDetail) =>
                arrItem.name !== item.name && arrItem.value !== item.value,
            ),
          );
        }}
      >
        ✕
      </button>
    </div>
  );

  return (
    <BaseForm label={label} tooltip={tooltip}>
      <div className="flex flex-column">{defaultValue.map(renderItem)}</div>
      <div className="relative mv3 flex w-100">
        <div className="mr3 flex-auto">
          <Select
            label="Name"
            field={['name']}
            id="name"
            defaultValue={name}
            onValueChange={(_: any, name: string) => setName(name)}
            options={arrToOpts(DetailOptions.concat(['Other…']))}
          />
          {!DetailOptions.includes(name) && name ? (
            <Text
              label="Enter a value…"
              field={['name']}
              id="custom-name"
              defaultValue={value}
              onValueChange={(_: any, name: string) => setName(name)}
            />
          ) : null}
        </div>
        <Text
          className="mr3 flex-auto"
          label="Description"
          field={['description']}
          id="desc"
          defaultValue={value}
          onValueChange={(_: any, value: string) => setValue(value)}
        />
        <button
          disabled={!canAdd}
          type="button"
          className={`${
            canAdd ? 'gold dim pointer' : 'white-20'
          } b dib link bg-transparent bw0 pa3 w3`}
          onClick={onAdd}
        >
          Add
        </button>
      </div>
    </BaseForm>
  );
}
